import { yupResolver } from '@hookform/resolvers/yup';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useSplitTreatments } from '@splitsoftware/splitio-react';
import {
  FileAttachableType,
  getV1CompaniesCompanyIdSitesId,
  OrderUnitOfMeasure,
  RateOwnerType,
  RateType,
  WaypointType,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { isNil, isNull, isString, isUndefined, sortBy } from 'lodash-es';
import React, {
  forwardRef,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  ControllerProps,
  FieldErrors,
  FieldNamesMarkedBoolean,
  FormProvider,
  useForm,
} from 'react-hook-form';

import FileAttachmentsReadOnly from '~components/Files/FileAttachmentsReadOnly';
import { AutocompleteAsyncFormField } from '~components/FormFields/AutocompleteAsyncFormField';
import { AutocompleteFormField } from '~components/FormFields/AutocompleteFormField';
import { CreateOptionChip } from '~components/FormFields/CreateOptionChip';
import { PhoneCodeFlagInput } from '~components/FormFields/PhoneCodeFlagInput';
import { SingleDateTimeFormField } from '~components/FormFields/SingleDateTimeFormField';
import { TextFormField } from '~components/FormFields/TextFormField';
import { PseudoLink } from '~components/Helpers/PseudoLink';
import { Contacts } from '~components/Order/Contacts';
import { OrderCustomerDetails } from '~components/Order/OrderCustomerDetails';
import { PhaseSelection } from '~components/Order/PhaseSelection';
import { ProjectDetails } from '~components/Order/ProjectDetails';
import { SiteSelection } from '~components/Order/SiteSelection/SiteSelection';
import { NoteTypes } from '~constants/enums';
import { FeatureFlags } from '~constants/featureFlags';
import { Notes } from '~formsShared/components/Notes';
import { FormStateChangeProps } from '~formsShared/types';
import { useAllowedUnitOfMeasureOptions } from '~hooks/useAllowedUnitOfMeasureOptions';
import { ContactItem } from '~hooks/useContact/models/ContactItem';
import { BasicDepartment } from '~hooks/useDepartment/models';
import { EquipmentTypeahead, EquipmentTypeItem } from '~hooks/useEquipment/models';
import useEquipment from '~hooks/useEquipment/useEquipment';
import { Job, JobFormPropsWithRates } from '~hooks/useJob/models';
import { useJob } from '~hooks/useJob/useJob';
import useLatestRequestSignal from '~hooks/useLatestRequestSignal/useLatestRequestSignal';
import { Material } from '~hooks/useMaterials/models';
import useMaterials from '~hooks/useMaterials/useMaterials';
import { Order } from '~hooks/useOrders/models';
import { useOrders } from '~hooks/useOrders/useOrders';
import { Phase, Project } from '~hooks/useProjects/models';
import useProjects from '~hooks/useProjects/useProjects';
import { RateBasicWithValue } from '~hooks/useRates/models';
import { useRates } from '~hooks/useRates/useRates';
import { Site, WayPoint } from '~hooks/useSites/models';
import HiredTruckLoads from '~pages/Dispatch/components/job-form/HiredTruckLoads';
import JobFreightRateRow from '~pages/Dispatch/components/JobFreightRateRow';
import {
  makeCustomerSpecificJobFormSchema,
  setDefaultJobValues,
} from '~pages/Dispatch/JobFormSchema';
import { fetchParentAddOnAssignmentsForJob } from '~pages/Dispatch/utils/fetchParentAddOnAssignmentsForJob';
import { AddOnAssignmentSchema } from '~pages/Sales/Orders/NewOrderForm/components/AutomaticAddOns';
import { RateDetailsDTO } from '~pages/Sales/Orders/NewOrderForm/schema';
import {
  createWaypoint,
  rateToRateBasicWithValue,
  shouldEnableRecurringLoads,
} from '~pages/Sales/Orders/NewOrderForm/utils';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { Nullable } from '~types/Nullable';
import {
  JobPayCommission,
  jobPayCommission,
  resolveRateDisabledReasons,
  resolveRateEditableSides,
} from '~utils/jobs/job-utils';
import { userCompanyOwnsOrder } from '~utils/orders';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import FileAttachments, {
  FileAttachmentsHandler,
} from '../../formsShared/components/FileAttachments';
import { serviceIsHiredTruck } from '../Sales/Orders/newOrderFormSchema';
import {
  getHaulerRateTypeOptions,
  getRateTypeLabel,
} from '../Settings/RatesManagement/rateUtils';
import { jobStatesAllowingEquipmentRemoval } from './components/EquipmentSelector';
import useJobScheduleRates, { JobRateInputHandler } from './hooks/useJobScheduleRates';
import JobDetailsV2 from './JobEditFormV2/components/JobDetailsV2';
import { buildJobEditRatePayload, computeJobConditionsChanged } from './utils';

interface FormProps {
  defaultJob: Job;
  onFormStateChange: ({ isValid, isDirty }: FormStateChangeProps) => void;
}

export type InitialRateInfo = {
  commission?: JobPayCommission;
  rate: RateBasicWithValue | null;
  rateType: RateType | null;
  rateValue: number | null;
};

type JobEditFormDirtyFields = Partial<
  Readonly<FieldNamesMarkedBoolean<JobFormPropsWithRates>>
>;

type JobEditFormSubmitCallback = (
  data: JobFormPropsWithRates,
  dirtyFields: JobEditFormDirtyFields,
) => void;

export type JobEditFormHandler = {
  submit?: (callback: JobEditFormSubmitCallback) => void;
  resetForm?: (callback: () => void) => void;
  fileAttachmentsOnSubmit?: (jobId: string) => Promise<void>;
  fileAttachmentsOnClose?: () => Promise<void>;
  isRatesDirty: boolean;
  conditionsChanged: boolean;
  isOrderOwner: boolean;
};

const getEquipmentLabel = (equipment?: EquipmentTypeahead) => {
  if (!equipment) return '';
  return `${equipment?.companyShare?.externalId || equipment?.name} • ${equipment?.equipmentType?.name}`;
};

const JobEditForm = forwardRef(function JobEditForm(
  { defaultJob, onFormStateChange }: FormProps,
  ref: Ref<JobEditFormHandler>,
) {
  const theme = useTheme();
  const { treatments, isReady } = useSplitTreatments({
    names: [
      FeatureFlags.tomlinsonOrderModal,
      FeatureFlags.showHaulerRateTypeCard,
      FeatureFlags.vendorJobSplittingEnabled,
    ],
  });

  const tomlinsonOrderModalFeatureFlagEnabled =
    isReady && treatments[FeatureFlags.tomlinsonOrderModal].treatment === 'on';
  const showHaulerRateTypeCardFeatureFlagEnabled =
    isReady && treatments[FeatureFlags.showHaulerRateTypeCard].treatment === 'on';
  const shouldShowHaulerRateCard =
    showHaulerRateTypeCardFeatureFlagEnabled || tomlinsonOrderModalFeatureFlagEnabled;
  const vendorJobSplittingEnabled =
    isReady && treatments[FeatureFlags.vendorJobSplittingEnabled].treatment === 'on';

  const fileAttachmentsRef = useRef<FileAttachmentsHandler>(null);
  const unitOfMeasureOptions = useAllowedUnitOfMeasureOptions();
  const { userStore, toasterStore } = useStores();
  const { getEquipmentTypesByCompanyId, getJobEquipmentTypeahead } = useEquipment();
  const issueEquipmentTypesSignal = useLatestRequestSignal();
  const [truckPickerIsEmpty, setTruckPickerIsEmpty] = useState(false);
  const runEquipmentTypeahead = useCallback(
    async (
      options: Parameters<typeof getJobEquipmentTypeahead>[0],
      reportIsEmpty?: (isEmpty: boolean) => void,
    ) => {
      const isUnfiltered = !options.query;

      try {
        const result = await getJobEquipmentTypeahead(options);

        if (isUnfiltered) reportIsEmpty?.(result.data.length === 0);

        return result;
      } catch {
        // Error is already handled in the hook
        if (isUnfiltered) reportIsEmpty?.(false);

        return { data: [], pagination: undefined };
      }
    },
    [getJobEquipmentTypeahead],
  );
  const getJobTruckTypeahead = useCallback(
    (options: Parameters<typeof getJobEquipmentTypeahead>[0]) =>
      runEquipmentTypeahead(options, setTruckPickerIsEmpty),
    [runEquipmentTypeahead],
  );
  const [isEquipmentLoading, setIsEquipmentLoading] = useState(false);
  const { createMaterialByCompanyId, getMaterialsByCompanyIdTypeahead } = useMaterials();
  const { getJobLoads } = useJob();

  const [isRatesDirty, setIsRatesDirty] = useState(false);

  const [showPickUpContact, setShowPickUpContact] = useState<boolean>(false);
  const [showDropOffContact, setShowDropOffContact] = useState<boolean>(false);
  const [isPickUpEditCtaDisabled, setIsPickUpEditCtaDisabled] = useState(true);
  const [isDropOffEditCtaDisabled, setIsDropOffEditCtaDisabled] = useState(true);

  const [equipmentTypesOptions, setEquipmentTypesOptions] = useState<EquipmentTypeItem[]>(
    [],
  );
  const isHiredTruck = serviceIsHiredTruck(defaultJob.service ?? null);
  const defaultPickUpWaypoint = defaultJob?.pickUpWayPoints?.[0];
  const defaultDropOffWaypoint = defaultJob?.dropOffWayPoints?.[0];
  const defaultStagingWaypoint = defaultJob?.stagingWayPoints?.[0];
  const isEquipmentRemovable = Boolean(
    jobStatesAllowingEquipmentRemoval.includes(defaultJob.status),
  );

  const [pickUpWaypoint, setPickUpWaypoint] =
    useState<Nullable<WayPoint>>(defaultPickUpWaypoint);
  const [dropOffWaypoint, setDropOffWaypoint] =
    useState<Nullable<WayPoint>>(defaultDropOffWaypoint);
  const [stagingWaypoint, setStagingWaypoint] =
    useState<Nullable<WayPoint>>(defaultStagingWaypoint);

  const { getAccountRatesUsingOwnerType } = useRates();

  const formSchema = makeCustomerSpecificJobFormSchema(
    tomlinsonOrderModalFeatureFlagEnabled,
    vendorJobSplittingEnabled,
  );

  const formMethods = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    context: {
      isHiredTruckService: isHiredTruck,
    },
    defaultValues: {
      ...setDefaultJobValues(defaultJob),
      account: defaultJob.order?.account,
      department: null as unknown as BasicDepartment,
      project: defaultJob.project,
      projectExternalId: '',
      phase: defaultJob.phase,
      phaseCode: null,
      name: defaultJob.order?.name,
      poJobNumber: defaultJob.order?.poJobNumber,
      zone: defaultJob.order?.zone,
      externalId: '',
      dispatchNumber: defaultJob.order?.dispatchNumber?.name,
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    trigger,
    setError,
    getValues,
    formState: { errors, isValid, isDirty, dirtyFields },
  } = formMethods;

  const companyId = getEffectiveUserCompanyId(userStore);
  const viewerIsVendorOnJob = Boolean(
    defaultJob.company?.id && defaultJob.company.id !== companyId,
  );
  const noSelectableTrucksNotice = defaultJob.company?.legalName
    ? t('dispatch.job.no_selectable_trucks_named', {
        company: defaultJob.company.legalName,
        // A company name containing "&" would otherwise render as `&amp;`.
        interpolation: { escapeValue: false },
      })
    : t('dispatch.job.no_selectable_trucks');
  const isAssignedToVendor = Boolean(defaultJob.vendorJobAssignment?.id);
  const isAssignedToDriver = !isAssignedToVendor && Boolean(defaultJob.driver?.id);
  const rateEditable = resolveRateEditableSides(defaultJob, isAssignedToVendor);
  const watchJobPhase = watch('phase');
  const watchedMaterial = watch('material') as Material | null;
  const previousMaterialIdRef = useRef(defaultJob.material?.id);
  const watchedPickUpWaypointSite = watch('pickUpWayPoint.site');
  const watchedDropOffWaypointSite = watch('dropOffWayPoint.site');
  const watchedJobUnitOfMeasure = watch('unitOfMeasure');
  const watchedEquipmentTypeGrossCapacityUnitOfMeasure = watch(
    'equipmentTypeGrossCapacityUnitOfMeasure',
  );
  const watchedEquipmentTypeGrossCapacity = watch('equipmentTypeGrossCapacity') as
    | string
    | number
    | null;
  const watchedQuantity = watch('quantity') as string | number | null;

  const [lastSelectedPickUpPhone, setLastSelectedPickUpPhone] = useState<string>('');
  const [lastSelectedDropOffPhone, setLastSelectedDropOffPhone] = useState<string>('');
  const [pickUpcontacts, setPickUpcontacts] = useState<ContactItem[]>([]);
  const [dropOffcontacts, setDropOffcontacts] = useState<ContactItem[]>([]);

  useEffect(() => {
    const signal = issueEquipmentTypesSignal();
    setEquipmentTypesOptions([]);

    if (!companyId) {
      setIsEquipmentLoading(false);
      return;
    }

    const loadEquipmentTypes = async () => {
      setIsEquipmentLoading(true);

      try {
        const { data } = await getEquipmentTypesByCompanyId({ companyId, signal });
        if (signal.aborted) return;

        setEquipmentTypesOptions(sortBy(data, 'name'));
      } catch {
        // Error is already handled in the hook
      } finally {
        if (!signal.aborted) {
          setIsEquipmentLoading(false);
        }
      }
    };

    void loadEquipmentTypes();
  }, [companyId, getEquipmentTypesByCompanyId, issueEquipmentTypesSignal]);

  useEffect(() => {
    if (defaultJob?.id) {
      getJobLoads(defaultJob.id).then((loads) => {
        setValue('loads', loads);
        trigger('loads');
      });
    }
  }, [defaultJob?.id]);

  useEffect(() => {
    if (defaultJob?.orderId) {
      // TODO: THIS IS TOTALLY BROKEN!
      // this used to be `${...}` but i changed it to String(...) (see TRE-8563).
      // String(null) is 'null', not null, and String(undefined) is 'undefined', not
      // undefined, so i don't think this code was ever doing what it was intended to do.
      // however - i don't feel comfortable changing this behavior because i cannot
      // figure out how to test. caveat emptor!
      if (defaultJob?.pickUpWayPoints?.[0]?.site?.id !== watchedPickUpWaypointSite?.id) {
        setValue('pickUpWayPoint.id', String(defaultJob?.pickUpWayPoints?.[0]?.id));
      }
      if (
        defaultJob?.dropOffWayPoints?.[0]?.site?.id !== watchedDropOffWaypointSite?.id
      ) {
        setValue('dropOffWayPoint.id', String(defaultJob?.dropOffWayPoints?.[0]?.id));
      }
    }
  }, [watchedPickUpWaypointSite, watchedDropOffWaypointSite]);

  // Declared up here (rather than alongside the other order/project state
  // below) so the submit closure inside `useImperativeHandle` can list
  // `isOrderOwner` in its dep array without hitting a TDZ ReferenceError.
  const [orderData, setOrderData] = useState<Order | null>(null);
  const isOrderOwner = orderData ? userCompanyOwnsOrder(userStore, orderData) : false;
  const rateDisabledReasons = resolveRateDisabledReasons(rateEditable, isOrderOwner);

  useImperativeHandle(
    ref,
    () => ({
      submit: async (callBack: JobEditFormSubmitCallback) => {
        const activeCustomerRef = customerJobRateInputRef.current;
        const activeVendorOrDriverRef = vendorOrDriverJobRateInputRef.current;
        const hasInvalidCommission = [activeCustomerRef, activeVendorOrDriverRef].some(
          (input) => input?.validateCommission?.() === false,
        );
        if (hasInvalidCommission) {
          return;
        }

        const customerRateDetails = await new Promise<{
          data: RateDetailsDTO | undefined;
          addOnAssignments: AddOnAssignmentSchema[];
        }>((resolve) => {
          if (activeCustomerRef?.onSubmit) {
            activeCustomerRef.onSubmit((data, addOnAssignments) => {
              resolve({ data, addOnAssignments });
            });
          } else if (shouldShowHaulerRateCard) {
            resolve({
              data: { targetRate: null, targetRateType: null, targetRateValue: null },
              addOnAssignments: [],
            });
          } else {
            resolve({ data: undefined, addOnAssignments: [] });
          }
        });

        const vendorOrDriverRateDetails = await new Promise<{
          data: RateDetailsDTO | undefined;
          addOnAssignments: AddOnAssignmentSchema[];
        }>((resolve) => {
          if (activeVendorOrDriverRef?.onSubmit) {
            activeVendorOrDriverRef.onSubmit((data, addOnAssignments) => {
              resolve({ data, addOnAssignments });
            });
          } else if (shouldShowHaulerRateCard) {
            resolve({
              data: { targetRate: null, targetRateType: null, targetRateValue: null },
              addOnAssignments: [],
            });
          } else {
            resolve({ data: undefined, addOnAssignments: [] });
          }
        });

        const isAssigned = isAssignedToVendor || isAssignedToDriver;
        const vendorOrDriverRateInfoIsValid = isAssigned
          ? !isUndefined(vendorOrDriverRateDetails)
          : true;

        if (customerRateDetails && vendorOrDriverRateInfoIsValid) {
          const ratePayload = buildJobEditRatePayload({
            customerRateDetails,
            vendorOrDriverRateDetails,
            isAssignedToVendor,
            isAssignedToDriver,
          });

          handleSubmit((formData) => {
            callBack(
              {
                ...formData,
                // Only an order owner's explicit schedule change may stamp this field.
                ...(isOrderOwner && dirtyFields.siteRateSchedule
                  ? {}
                  : { siteRateSchedule: undefined }),
                // The truck type is only sent when the user edited it. Passing
                // the form's own value on every save let a form whose state had
                // not caught up with the loaded job clear the job's truck type,
                // which then read as blank on the Dispatch Jobs tab.
                ...(dirtyFields.requestedEquipmentTypeName
                  ? {}
                  : { requestedEquipmentTypeName: undefined }),
                phase: formData.phase,
                ...ratePayload,
                customerJobAssignment: defaultJob.customerJobAssignment,
                vendorJobAssignment: defaultJob.vendorJobAssignment,
              },
              dirtyFields,
            );
          })();
        }
      },
      resetForm(callBack?: () => void) {
        reset();
        setIsRatesDirty(false);
        callBack?.();
      },
      fileAttachmentsOnSubmit: async (jobId: string) => {
        await fileAttachmentsRef.current?.onSubmit(jobId);
      },
      fileAttachmentsOnClose: async () => {
        await fileAttachmentsRef.current?.onCancel();
      },
      isRatesDirty,
      isOrderOwner,
      conditionsChanged: computeJobConditionsChanged({
        isDirtyEquipmentTypeName: Boolean(dirtyFields.requestedEquipmentTypeName),
        isDirtyMaterial: Boolean(dirtyFields.material),
        defaultPickupSiteId: defaultPickUpWaypoint?.site?.id,
        currentPickupSiteId: watchedPickUpWaypointSite?.id,
        defaultDropoffSiteId: defaultDropOffWaypoint?.site?.id,
        currentDropoffSiteId: watchedDropOffWaypointSite?.id,
      }),
    }),
    [
      isAssignedToVendor,
      isAssignedToDriver,
      isOrderOwner,
      shouldShowHaulerRateCard,
      dirtyFields,
      isRatesDirty,
      watchedPickUpWaypointSite,
      watchedDropOffWaypointSite,
    ],
  );

  useEffect(() => {
    onFormStateChange({ isDirty, isValid });
  }, [isValid, isDirty]);

  useEffect(() => {
    if (defaultPickUpWaypoint?.contact?.name) {
      setShowPickUpContact(true);
    }
    if (defaultDropOffWaypoint?.contact?.name) {
      setShowDropOffContact(true);
    }
  }, []);

  const [tmpMaterial, setTmpMaterial] = useState<string>('');
  const [isMaterialLoading, setIsMaterialLoading] = useState(false);
  const createMaterial = async () => {
    if (!userStore.userCompany?.id) {
      toasterStore.push(
        alert(t('form_validation_errors.no_user_company'), AlertTypes.error),
      );
    }

    try {
      setIsMaterialLoading(true);
      const material = await createMaterialByCompanyId({
        companyId: companyId,
        material: { name: tmpMaterial } as unknown as Material,
      });
      toasterStore.push(
        alert(
          t('administration.material.material_created', {
            name: material.name,
          }),
          AlertTypes.success,
        ),
      );
      setValue('material', material);
      trigger('material');
    } finally {
      setIsMaterialLoading(false);
    }
  };

  const { getOrderById } = useOrders();
  const { getProjectById } = useProjects();
  const [projectData, setProjectData] = useState<Project | null>(null);
  const isOrderEditable = Boolean(orderData?.editable);

  const customerJobRateInputRef = useRef<JobRateInputHandler>(null);
  const vendorOrDriverJobRateInputRef = useRef<JobRateInputHandler>(null);

  const {
    applySchedule,
    clearSchedule,
    unlinkSchedule,
    onCustomerRateFieldsChange,
    onAssigneeRateFieldsChange,
  } = useJobScheduleRates({
    getSchedule: () => getValues('siteRateSchedule') ?? null,
    setSchedule: (schedule) =>
      setValue('siteRateSchedule', schedule, { shouldDirty: true }),
    isAssignedToVendor,
    customerRateDetailsRef: customerJobRateInputRef,
    vendorOrDriverRateDetailsRef: vendorOrDriverJobRateInputRef,
  });

  const [initialRates, setInitialRates] = useState<
    Partial<Record<RateOwnerType, InitialRateInfo | null>>
  >({});

  const hasInitialCustomerRate = RateOwnerType.CUSTOMER in initialRates;
  const hasInitialVendorRate = RateOwnerType.VENDOR in initialRates;
  const hasInitialDriverRate = RateOwnerType.DRIVER in initialRates;

  const getDefaultRate = useCallback(
    (ownerType: RateOwnerType) => {
      const rateInfo: InitialRateInfo = { rate: null, rateType: null, rateValue: null };

      if (ownerType === RateOwnerType.DRIVER) {
        rateInfo.commission = jobPayCommission(defaultJob, 'driver');
        rateInfo.rate = defaultJob.driverRate ?? null;
        rateInfo.rateType = defaultJob.driverRateType ?? null;
        rateInfo.rateValue = defaultJob.driverRateValue ?? null;
      } else if (ownerType === RateOwnerType.CUSTOMER) {
        rateInfo.rate = defaultJob.customerJobAssignment?.rate ?? null;
        rateInfo.rateType = defaultJob.customerJobAssignment?.rateType ?? null;
        rateInfo.rateValue = defaultJob.customerJobAssignment?.rateValue ?? null;
      } else {
        rateInfo.commission = jobPayCommission(defaultJob, 'vendor');
        rateInfo.rate = defaultJob.vendorJobAssignment?.rate ?? null;
        rateInfo.rateType = defaultJob.vendorJobAssignment?.rateType ?? null;
        rateInfo.rateValue = defaultJob.vendorJobAssignment?.rateValue ?? null;
      }

      // Rate card
      if (rateInfo.rate) {
        return { ...rateInfo };
      }

      // Manual rate
      if (!isNull(rateInfo.rateType) && !isNull(rateInfo.rateValue)) {
        return { ...rateInfo, rate: null };
      }

      // A rate-less job can still carry a commission, and returning null here
      // would drop it before the rate row can show it.
      if (!isNil(rateInfo.commission?.percentage)) {
        return { ...rateInfo };
      }

      return null;
    },
    [
      defaultJob.customerJobAssignment,
      defaultJob.driverCommissionLocked,
      defaultJob.driverCommissionResolution,
      defaultJob.driverPayCommissionPercentage,
      defaultJob.driverRate,
      defaultJob.driverRateType,
      defaultJob.driverRateValue,
      defaultJob.inheritedDriverPayCommissionPercentage,
      defaultJob.netDriverRate,
      defaultJob.vendorJobAssignment,
    ],
  );

  const fetchRates = useCallback(
    async (
      ownerType: RateOwnerType,
      params: {
        accountId?: string;
        driverId?: string;
        projectId?: string;
        serviceId?: string;
      },
      args?: { query: string; cursor: string },
    ) => {
      if (companyId) {
        const response = await getAccountRatesUsingOwnerType({
          companyId,
          ownerType,
          includeDefaultRates: true,
          accountId: params.accountId,
          driverId: params.driverId,
          projectId: params.projectId,
          serviceId: params.serviceId,
          query: args?.query,
          cursor: args?.cursor,
          limit: 15,
        });

        const basicRates = response?.data.map((rate) => rateToRateBasicWithValue(rate));

        setInitialRates((state) => ({
          ...state,
          [ownerType]: getDefaultRate(ownerType),
        }));

        return { ...response, data: basicRates };
      }
    },
    [companyId],
  );

  const fetchCustomerRates = useCallback(
    (args?: { query: string; cursor: string }) => {
      return fetchRates(
        RateOwnerType.CUSTOMER,
        {
          accountId: defaultJob.customerJobAssignment?.customerAccount?.id,
          projectId: defaultJob.project?.id,
          serviceId: defaultJob.service?.id,
        },
        args,
      );
    },
    [
      fetchRates,
      defaultJob.customerJobAssignment?.customerAccount?.id,
      defaultJob.project?.id,
      defaultJob.service?.id,
    ],
  );

  const fetchVendorRates = useCallback(
    (args?: { query: string; cursor: string }) => {
      return fetchRates(
        RateOwnerType.VENDOR,
        {
          accountId: defaultJob.vendorJobAssignment?.vendorAccount?.id,
          projectId: defaultJob.project?.id,
          serviceId: defaultJob.service?.id,
        },
        args,
      );
    },
    [
      fetchRates,
      defaultJob.vendorJobAssignment?.vendorAccount?.id,
      defaultJob.project?.id,
      defaultJob.service?.id,
    ],
  );

  const fetchDriverRates = useCallback(
    (args?: { query: string; cursor: string }) => {
      return fetchRates(
        RateOwnerType.DRIVER,
        {
          driverId: defaultJob.driver?.id,
          projectId: defaultJob.project?.id,
          serviceId: defaultJob.service?.id,
        },
        args,
      );
    },
    [fetchRates, defaultJob.driver?.id, defaultJob.project?.id, defaultJob.service?.id],
  );

  const fetchParentAddOnAssignments =
    (rateOwnerType: RateOwnerType) => (signal?: AbortSignal) =>
      fetchParentAddOnAssignmentsForJob(companyId, rateOwnerType, defaultJob, signal);

  useEffect(() => {
    if (!hasInitialCustomerRate) {
      fetchCustomerRates();
    }

    if (isAssignedToVendor && !hasInitialVendorRate) {
      fetchVendorRates();
    }

    if (isAssignedToDriver && !hasInitialDriverRate) {
      fetchDriverRates();
    }
  }, [
    fetchCustomerRates,
    fetchVendorRates,
    fetchDriverRates,
    hasInitialCustomerRate,
    hasInitialVendorRate,
    hasInitialDriverRate,
    isAssignedToVendor,
    isAssignedToDriver,
  ]);

  useEffect(() => {
    if (defaultJob) {
      getOrderById(defaultJob.order?.id || '').then((order) => {
        setOrderData(order);
      });
    }
  }, [defaultJob]);

  useEffect(() => {
    // The fetched project is only used for the order-owner-only
    // material→phase auto-fill below, and `GET /v1/projects/:id` 404s for
    // sub-vendors and drivers who don't have access to the project. Wait
    // for `orderData` to load (so `isOrderOwner` is meaningful) and skip
    // the call entirely when the viewer is not the order owner.
    if (!orderData || !isOrderOwner) return;
    const fetchProject = async () => {
      if (defaultJob?.project?.id) {
        const project = await getProjectById(defaultJob.project.id);
        if (project) setProjectData(project);
      }
    };
    fetchProject();
  }, [defaultJob?.project?.id, orderData, isOrderOwner]);

  // Auto-fill phase when material changes based on project material-phase link
  useEffect(() => {
    const materialId = watchedMaterial?.id;
    if (materialId === previousMaterialIdRef.current) return;
    previousMaterialIdRef.current = materialId;

    if (!materialId || !projectData?.projectMaterialTotals?.length || !orderData) return;

    const pmt = projectData.projectMaterialTotals.find(
      (item) => item.material?.id === materialId,
    );
    if (!pmt?.phase) return;

    const orderPhase = orderData.phases?.find(
      (p) => p.name === pmt.phase?.name && p.code === pmt.phase?.code,
    );
    if (orderPhase) {
      setValue('phase', new Phase(orderPhase.id, orderPhase.name, orderPhase.code, true));
    }
  }, [watchedMaterial?.id, projectData?.id, orderData?.id]);

  useEffect(() => {
    defaultJob?.project?.externalId &&
      setValue('projectExternalId', defaultJob?.project?.externalId);
    defaultJob?.project?.id && setValue('project', defaultJob?.project);
    defaultJob?.project?.notes && setValue('projectNotes', defaultJob?.project?.notes);
  }, [defaultJob]);

  useEffect(() => {
    orderData?.account?.id && setValue('account', orderData?.account);
    orderData?.department?.id && setValue('department', orderData?.department);
    orderData?.name && setValue('name', orderData?.name || '');
    orderData?.poJobNumber && setValue('poJobNumber', orderData?.poJobNumber || '');
    orderData?.zone && setValue('zone', orderData?.zone || '');
    orderData?.externalId && setValue('externalId', orderData?.externalId || '');
    orderData?.notes && setValue('orderNotes', orderData?.notes || '');
    orderData?.dispatchNumber &&
      setValue('dispatchNumber', orderData?.dispatchNumber?.name || '');

    if (orderData?.jobQuantity && !defaultJob?.quantity) {
      setValue('quantity', orderData?.jobQuantity);
    }
    if (orderData?.jobUnitOfMeasure && !defaultJob?.unitOfMeasure) {
      setValue('unitOfMeasure', orderData?.jobUnitOfMeasure);
    }
    if (
      orderData?.equipmentTypeGrossCapacity &&
      !defaultJob?.equipmentTypeGrossCapacity
    ) {
      setValue('equipmentTypeGrossCapacity', orderData?.equipmentTypeGrossCapacity);
    }
    if (
      orderData?.equipmentTypeGrossCapacityUnitOfMeasure &&
      !defaultJob?.equipmentTypeGrossCapacityUnitOfMeasure
    ) {
      setValue(
        'equipmentTypeGrossCapacityUnitOfMeasure',
        orderData?.equipmentTypeGrossCapacityUnitOfMeasure,
      );
    }
  }, [orderData, defaultJob]);

  useEffect(() => {
    const requestedLoadsCountMessage =
      formMethods.formState.errors?.requestedLoadsCount?.message;

    if (isString(requestedLoadsCountMessage)) {
      formMethods.setError('requestedLoadsCount', {
        type: 'manual',
        message: requestedLoadsCountMessage,
      });
    }
  }, [formMethods.formState.errors]);

  const toggleOnPickUpContact = () => {
    setShowPickUpContact(true);
  };

  const toggleOffPickUpContact = () => {
    setValue('pickUpWayPoint.contact', {
      name: '',
      phone: '',
    } as ContactItem);

    trigger('pickUpWayPoint.contact');
    setShowPickUpContact(false);
  };

  const toggleOnDropOffContact = () => {
    setShowDropOffContact(true);
  };

  const toggleOffDropOffContact = () => {
    setValue('dropOffWayPoint.contact', {
      name: '',
      phone: '',
    } as ContactItem);
    trigger('dropOffWayPoint.contact');
    setShowDropOffContact(false);
  };

  const pickupDidChange =
    defaultPickUpWaypoint?.site?.id !== watchedPickUpWaypointSite?.id;
  const shouldFetchPickupContacts = isOrderEditable ? true : pickupDidChange;

  const dropOffDidChange =
    defaultDropOffWaypoint?.site?.id !== watchedDropOffWaypointSite?.id;
  const shouldFetchDropoffContacts = isOrderEditable ? true : dropOffDidChange;

  const setSiteContact = async (
    waypointType: 'pickUpWayPoint' | 'dropOffWayPoint',
    siteId?: string,
    isDiscarded?: boolean,
  ) => {
    const isPickUpWayPoint = waypointType === 'pickUpWayPoint';

    if (siteId && !isDiscarded) {
      let siteContacts: ContactItem[] = [];

      if (
        (isPickUpWayPoint && shouldFetchPickupContacts) ||
        (!isPickUpWayPoint && shouldFetchDropoffContacts)
      ) {
        try {
          const response = await getV1CompaniesCompanyIdSitesId({
            path: { 'company-id': companyId, id: siteId },
          });
          const site = Site.parse(response.data.data);
          const userOwnsSite = site.company?.id === companyId;

          siteContacts = [...site.contacts];

          if (isPickUpWayPoint) {
            setIsPickUpEditCtaDisabled(!userOwnsSite);
          } else {
            setIsDropOffEditCtaDisabled(!userOwnsSite);
          }
        } catch (error) {
          if (isPickUpWayPoint) {
            setIsPickUpEditCtaDisabled(false);
          } else {
            setIsDropOffEditCtaDisabled(false);
          }

          connection.handleRequestError(
            error,
            t('error_messages.sites.failed_to_fetch_site'),
          );
        }
      } else {
        if (isPickUpWayPoint) {
          setIsPickUpEditCtaDisabled(true);
        } else {
          setIsDropOffEditCtaDisabled(true);
        }
      }

      if (siteContacts.length > 0) {
        if (isPickUpWayPoint) {
          setPickUpcontacts(siteContacts);
          toggleOnPickUpContact();
          setLastSelectedPickUpPhone(siteContacts[0].phone || '');
          setPickUpWaypoint({
            ...pickUpWaypoint,
            contact: {
              name: siteContacts[0].name,
              phone: siteContacts[0].phone,
            },
          } as WayPoint);
        } else {
          setDropOffcontacts(siteContacts);
          toggleOnDropOffContact();
          setLastSelectedDropOffPhone(siteContacts[0].phone || '');
          setDropOffWaypoint({
            ...dropOffWaypoint,
            contact: {
              name: siteContacts[0].name,
              phone: siteContacts[0].phone,
            },
          } as WayPoint);
        }
        setValue(`${waypointType}.contact`, {
          name: siteContacts[0].name,
          phone: siteContacts[0].phone,
        } as ContactItem);
      }
    } else {
      if (isPickUpWayPoint) {
        setIsPickUpEditCtaDisabled(false);
      } else {
        setIsDropOffEditCtaDisabled(false);
      }
    }
  };

  useEffect(() => {
    if (!isDirty) return;
    if (watchedJobUnitOfMeasure?.id !== OrderUnitOfMeasure.LOAD) {
      const isRunning = shouldEnableRecurringLoads(
        watchedQuantity,
        watchedEquipmentTypeGrossCapacity,
        watchedJobUnitOfMeasure?.name,
      );
      setValue('recurringLoads', isRunning);
    }
  }, [watchedJobUnitOfMeasure, watchedQuantity, watchedEquipmentTypeGrossCapacity]);

  useEffect(() => {
    setSiteContact(
      'pickUpWayPoint',
      watchedPickUpWaypointSite?.id,
      Boolean(defaultPickUpWaypoint?.site?.discardedAt),
    );
  }, [
    defaultPickUpWaypoint?.contact?.name,
    defaultPickUpWaypoint?.site?.discardedAt,
    watchedPickUpWaypointSite?.id,
    shouldFetchPickupContacts,
  ]);

  useEffect(() => {
    setSiteContact(
      'dropOffWayPoint',
      watchedDropOffWaypointSite?.id,
      Boolean(defaultDropOffWaypoint?.site?.discardedAt),
    );
  }, [
    defaultDropOffWaypoint?.contact?.name,
    defaultDropOffWaypoint?.site?.discardedAt,
    watchedDropOffWaypointSite?.id,
    shouldFetchDropoffContacts,
  ]);

  useEffect(() => {
    if (watchedEquipmentTypeGrossCapacityUnitOfMeasure?.id === OrderUnitOfMeasure.LOAD) {
      setValue('equipmentTypeGrossCapacity', 1);
    }
  }, [watchedEquipmentTypeGrossCapacityUnitOfMeasure]);

  const equipmentBlockV2 = (
    <>
      <Grid size={{ xs: 6 }}>
        <AutocompleteFormField
          control={control}
          disabled={!isOrderEditable}
          errors={errors}
          getLabel={(item) => item}
          getValue={(item) => item}
          isRequired={false}
          label={`${t('dispatch.order.order_details_v2.truck_type')}`}
          list={equipmentTypesOptions.map((item) => item.name)}
          loading={isEquipmentLoading}
          multiple={false}
          name="requestedEquipmentTypeName"
          dataUi={`page-dispatch__job-edit-form__requested-equipment-type-name-input`}
        />
      </Grid>
      <Grid size={{ xs: 3 }}>
        <AutocompleteFormField
          clearable
          control={control}
          disabled={!isOrderEditable}
          errors={errors}
          getLabel={(item) => item?.name || ''}
          getValue={(item) => item?.id || ''}
          label={t('form_fields.units')}
          list={unitOfMeasureOptions}
          name="equipmentTypeGrossCapacityUnitOfMeasure"
          dataUi={`page-dispatch__job-edit-form__equipment-type-gross-capacity-unit-of-measure-input`}
        />
      </Grid>
      <Grid size={{ xs: 3 }}>
        <TextFormField
          control={control}
          errors={errors}
          label={`${t('form_fields.truck_capacity')}`}
          name="equipmentTypeGrossCapacity"
          type="number"
          disabled={
            watchedEquipmentTypeGrossCapacityUnitOfMeasure?.id === OrderUnitOfMeasure.LOAD
          }
          dataUi={`page-dispatch__job-edit-form__equipment-type-gross-capacity-input`}
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Divider />
      </Grid>
      <Grid size={{ xs: 6 }}>
        <AutocompleteAsyncFormField
          control={control}
          errors={errors}
          name="equipment"
          getValue={(item) => item?.id || ''}
          getLabel={(item: EquipmentTypeahead) => (item ? getEquipmentLabel(item) : '')}
          asyncCallback={getJobTruckTypeahead}
          extraRequestOptions={{
            companyId: companyId,
            jobId: defaultJob.id,
          }}
          filterSelectedOptions={false}
          inputProps={{ 'data-test-id': 'job-edit-form__equipment-input' }}
          label={`${t('form_fields.truck')}`}
          clearable={isEquipmentRemovable}
          noOptionsText={
            viewerIsVendorOnJob && truckPickerIsEmpty ? (
              <Typography
                data-test-id="job-edit-form__empty-truck-picker-notice"
                variant="body2"
              >
                {noSelectableTrucksNotice}
              </Typography>
            ) : undefined
          }
          dataUi={`page-dispatch__job-edit-form__equipment-input`}
        />
      </Grid>
      <Grid size={{ xs: 6 }}>
        <AutocompleteAsyncFormField
          control={control}
          errors={errors}
          name={'additionalEquipment'}
          getValue={(item) => item?.id || ''}
          getLabel={(item: EquipmentTypeahead) => (item ? getEquipmentLabel(item) : '')}
          asyncCallback={runEquipmentTypeahead}
          extraRequestOptions={{
            companyId: companyId,
            jobId: defaultJob.id,
          }}
          filterSelectedOptions={false}
          inputProps={{ 'data-test-id': 'job-edit-form__additional-equipment-input' }}
          label={`${t('form_fields.trailer')}`}
          clearable
          dataUi={`page-dispatch__job-edit-form__additional-equipment-input`}
        />
      </Grid>
    </>
  );

  return (
    <Box component="div" sx={{ mt: 0, py: 2 }} data-test-id="job-order-form">
      <FormProvider {...formMethods}>
        <Grid container spacing={2}>
          {/*Left side*/}
          <Grid size={{ xs: 6 }}>
            {/* Project details */}
            <Card sx={{ p: 2, mb: 2 }}>
              <Typography variant="form-section-title" sx={{ mb: 1 }}>
                {t('order.form.project_details')}
              </Typography>
              <Grid container spacing={2}>
                <ProjectDetails
                  control={control}
                  errors={errors}
                  companyId={defaultJob.company?.id}
                  readOnly
                />

                {(orderData?.phases?.length ?? 0) > 0 && (
                  <PhaseSelection
                    isDisabled={!isOrderEditable}
                    canAdd={false}
                    onChange={(phase) => {
                      setValue(
                        'phase',
                        phase === null
                          ? null
                          : new Phase(phase.id, phase.name, phase.code, true),
                      );
                    }}
                    phaseOptions={orderData?.phases?.map((item) => ({
                      id: item.id,
                      name: item.name,
                      code: item.code,
                    }))}
                    selectedPhase={
                      watchJobPhase
                        ? {
                            id: watchJobPhase.id,
                            name: watchJobPhase.name,
                            code: watchJobPhase.code,
                          }
                        : undefined
                    }
                    canEdit={false}
                  />
                )}
              </Grid>
            </Card>
            {/* Order Customer Details */}
            <Card sx={{ p: 2, mb: 2 }}>
              <Typography variant="form-section-title" sx={{ mb: 1 }}>
                {t('order.form.order_customer_details')}
              </Typography>
              <OrderCustomerDetails
                control={control}
                errors={errors}
                disabled
                tomlinsonOrderModalFeatureFlagEnabled={
                  tomlinsonOrderModalFeatureFlagEnabled
                }
              />
            </Card>

            {/* Date and Time (v2) */}
            <Card sx={{ mb: 2, p: 2 }}>
              <Typography variant="form-section-title" sx={{ mb: 1 }}>
                {t('form_fields.date_and_time')}
              </Typography>

              <SingleDateTimeFormField
                control={control}
                dateLabel={t('order.form.job_start_date')}
                dense
                disabled={!isOrderEditable}
                errors={errors}
                isRequired
                name="jobStartAt"
                timeLabel={`${t('order.form.job_start_time')}`}
                dataUi={`page-dispatch__job-edit-form__job-start-at-input`}
              />

              <Box sx={{ width: '50%', mt: 2 }}>
                <TextFormField
                  control={control}
                  disabled={!isOrderEditable}
                  errors={errors}
                  label={t('form_fields.truck_hours')}
                  name="jobTime"
                  type="number"
                  dataUi={`page-dispatch__job-edit-form__job-time-input`}
                />
              </Box>
            </Card>

            {/* Material (v2) */}
            <Card sx={{ mb: 2, p: 2 }}>
              <Typography variant="form-section-title" sx={{ mb: 1 }}>
                {t('form_fields.material')}
              </Typography>
              <AutocompleteAsyncFormField
                asyncCallback={getMaterialsByCompanyIdTypeahead}
                clearable
                control={control}
                errors={errors}
                extraRequestOptions={{ companyId, orderId: defaultJob.order?.id }}
                getLabel={(item) => item.name || ''}
                getValue={(item) => item.id}
                label={t('form_fields.material')}
                name="material"
                noOptionsText={
                  tmpMaterial ? (
                    <CreateOptionChip
                      data-ui="page-dispatch__job-edit-form__create-material-button"
                      disabled={isMaterialLoading}
                      name={tmpMaterial || ''}
                      onClick={createMaterial}
                    />
                  ) : (
                    t('form_fields.no_options')
                  )
                }
                onInput={(_event, value) => setTmpMaterial(value)}
                dataUi={`page-dispatch__job-edit-form__material-input`}
              />
            </Card>

            {/* Job Details */}
            <JobDetailsV2 userOwnsJob={isOrderEditable}>{equipmentBlockV2}</JobDetailsV2>

            {/* Contacts */}
            <Contacts userOwnsDefaultData={isOrderEditable} defaultData={defaultJob} />
            {/* Loads */}
            <Card sx={{ mb: 2, p: 2, overflow: 'visible' }}>
              <Typography variant="form-section-title">{t('loads.title')}</Typography>
              <HiredTruckLoads jobData={defaultJob} isDisabled={!isOrderEditable} />
            </Card>
          </Grid>

          {/* Right side */}
          <Grid size={{ xs: 6 }}>
            <Card sx={{ p: 2, mb: 2, overflow: 'visible', minHeight: theme.spacing(20) }}>
              <Typography variant="form-section-title" sx={{ mb: 1 }}>
                {t('order.form.location_details')}
              </Typography>

              <SiteSelection
                allowSuggestions={false}
                companyId={companyId}
                disableDropOffEditCta={isDropOffEditCtaDisabled}
                disablePickUpEditCta={isPickUpEditCtaDisabled}
                disableSiteSelection={!isOrderEditable}
                disableStagingEditCta={!isOrderEditable}
                dropOffSite={dropOffWaypoint?.site}
                errors={errors}
                hideAddDefaultSiteAction={true}
                isRequired={!isHiredTruck}
                onlyShowDefaultSiteMarkers
                orderId={defaultJob.order?.id}
                pickUpSite={pickUpWaypoint?.site}
                projectId={defaultJob.project?.id}
                stagingSite={stagingWaypoint?.site}
                onPickUpSiteChange={(site) => {
                  let newPickUpWaypoint = null;
                  if (site) {
                    newPickUpWaypoint = createWaypoint(
                      site,
                      WaypointType.PICKUP,
                      10,
                      defaultJob?.pickUpWayPoints?.[0]?.id,
                      defaultJob?.pickUpWayPoints?.[0]?.contact ?? undefined,
                    );
                  } else if (!isHiredTruck && isDirty && !site) {
                    setError('pickUpWayPoint', {
                      types: { required: 'Site is required' },
                    });
                  }

                  setValue('pickUpWayPoint', newPickUpWaypoint, { shouldDirty: true });
                  setPickUpWaypoint(newPickUpWaypoint);
                  if (!newPickUpWaypoint) {
                    toggleOffPickUpContact();
                  }
                }}
                onDropOffSiteChange={(site) => {
                  let newDropOffWaypoint = null;
                  if (site) {
                    newDropOffWaypoint = createWaypoint(
                      site,
                      WaypointType.DROP_OFF,
                      20,
                      defaultJob?.dropOffWayPoints?.[0]?.id,
                      defaultJob?.dropOffWayPoints?.[0]?.contact ?? undefined,
                    );
                  } else if (!isHiredTruck && isDirty && !site) {
                    setError('dropOffWayPoint', {
                      types: { required: 'Site is required' },
                    });
                  }

                  setValue('dropOffWayPoint', newDropOffWaypoint, { shouldDirty: true });
                  setDropOffWaypoint(newDropOffWaypoint);
                  if (!newDropOffWaypoint) {
                    toggleOffDropOffContact();
                  }
                }}
                onStagingSiteChange={(site) => {
                  let newStagingWaypoint = null;
                  if (site) {
                    newStagingWaypoint = createWaypoint(
                      site,
                      WaypointType.STAGING,
                      15,
                      defaultStagingWaypoint?.id,
                    );
                  } else if (defaultStagingWaypoint?.id) {
                    // Preserve waypoint ID so deparseUpdate sends _destroy
                    newStagingWaypoint = {
                      id: defaultStagingWaypoint.id,
                    } as unknown as WayPoint;
                  }

                  setValue('stagingWayPoint', newStagingWaypoint, {
                    shouldDirty: true,
                  });
                  setStagingWaypoint(newStagingWaypoint);
                }}
                onlyDefaultSites={true}
              />
              {!showPickUpContact && watchedPickUpWaypointSite && (
                <PseudoLink
                  title={`+ ${t('order.form.add_pick_up_site_contact')}`}
                  action={toggleOnPickUpContact}
                />
              )}
              {watchedPickUpWaypointSite && (
                <Collapse in={showPickUpContact}>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6 }}>
                      <AutocompleteFormField
                        control={control}
                        name="pickUpWayPoint.contact.name"
                        errors={errors}
                        sx={{ mr: 2 }}
                        list={pickUpcontacts}
                        label={`${t('form_fields.name')}`}
                        getValue={(item) => item?.name || item}
                        getLabel={(item) => item?.name || item || ''}
                        clearable={true}
                        isRequired={false}
                        freeSolo
                        onSelect={(item) => {
                          setValue(
                            'pickUpWayPoint.contact.name',
                            item?.name || item || '',
                          );
                          setValue('pickUpWayPoint.contact.phone', item?.phone || '');
                          setLastSelectedPickUpPhone(item?.phone || '');
                        }}
                        dataUi={`page-dispatch__job-edit-form__pick-up-way-point-contact-name-input`}
                      />
                    </Grid>

                    <Grid size={{ xs: 6 }}>
                      <PhoneCodeFlagInput
                        key={`pickup-contact-phone-${showPickUpContact}-${lastSelectedPickUpPhone}`}
                        control={control as unknown as ControllerProps['control']}
                        errors={errors as unknown as FieldErrors}
                        name="pickUpWayPoint.contact.phone"
                        label={`${t('form_fields.phone')}`}
                        isRequired={false}
                      />
                    </Grid>
                  </Grid>
                  <PseudoLink
                    title={t('order.form.remove_pick_up_site_contact')}
                    sx={{ mt: 1 }}
                    action={toggleOffPickUpContact}
                  />
                </Collapse>
              )}
              {!showDropOffContact && watchedDropOffWaypointSite && (
                <PseudoLink
                  title={`+ ${t('order.form.add_drop_off_site_contact')}`}
                  action={toggleOnDropOffContact}
                />
              )}
              {watchedDropOffWaypointSite && (
                <Collapse in={showDropOffContact}>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6 }}>
                      <AutocompleteFormField
                        control={control}
                        name="dropOffWayPoint.contact.name"
                        errors={errors}
                        sx={{ mr: 2 }}
                        list={dropOffcontacts}
                        label={`${t('form_fields.name')}`}
                        getValue={(item) => item?.name || item}
                        getLabel={(item) => item?.name || item || ''}
                        clearable={true}
                        isRequired={false}
                        freeSolo
                        onSelect={(item) => {
                          setValue(
                            'dropOffWayPoint.contact.name',
                            item?.name || item || '',
                          );
                          setValue('dropOffWayPoint.contact.phone', item?.phone || '');
                          setLastSelectedDropOffPhone(item?.phone || '');
                        }}
                        dataUi={`page-dispatch__job-edit-form__drop-off-way-point-contact-name-input`}
                      />
                    </Grid>

                    <Grid size={{ xs: 6 }}>
                      <PhoneCodeFlagInput
                        key={`dropoff-contact-phone-${showDropOffContact}-${lastSelectedDropOffPhone}`}
                        control={control as unknown as ControllerProps['control']}
                        errors={errors as unknown as FieldErrors}
                        name="dropOffWayPoint.contact.phone"
                        label={`${t('form_fields.phone')}`}
                        isRequired={false}
                      />
                    </Grid>
                  </Grid>
                  <PseudoLink
                    title={t('order.form.remove_drop_off_site_contact')}
                    sx={{ mt: 1 }}
                    action={toggleOffDropOffContact}
                  />
                </Collapse>
              )}
            </Card>

            <Card sx={{ p: 2, mb: 2 }}>
              {shouldShowHaulerRateCard ? (
                <>
                  <Typography variant="form-section-title" sx={{ mb: 1 }}>
                    {t('order.form.rate_details')}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6 }}>
                      <AutocompleteFormField
                        clearable
                        control={control}
                        disablePortal={false}
                        errors={errors}
                        getLabel={(item) => item?.label ?? getRateTypeLabel(item)}
                        getValue={(item) => item?.value ?? item}
                        isCheckEnabled={false}
                        isRequired={tomlinsonOrderModalFeatureFlagEnabled}
                        label={`${t('form_fields.hauler_rate_type')}`}
                        list={getHaulerRateTypeOptions()}
                        name="haulerRateType"
                        dataUi={`page-dispatch__job-edit-form__hauler-rate-type-input`}
                      />
                    </Grid>
                  </Grid>
                </>
              ) : (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      mb: 1,
                    }}
                  >
                    <DescriptionOutlined
                      sx={{ color: theme.brandV2.colors.treadBlue, fontSize: 28 }}
                    />
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                      }}
                    >
                      {t('project.form.freight_rates')}
                    </Typography>
                  </Box>
                  <JobFreightRateRow
                    orderId={defaultJob.order?.id ?? null}
                    projectId={defaultJob.project?.id ?? null}
                    schedule={watch('siteRateSchedule') ?? null}
                    onSelectSchedule={applySchedule}
                    onClearSchedule={clearSchedule}
                    onUnlinkSchedule={unlinkSchedule}
                    isAssignedToVendor={isAssignedToVendor}
                    isAssignedToDriver={isAssignedToDriver}
                    jobStartAt={defaultJob.jobStartAt}
                    onDirtyStatusChange={(isDirty: boolean) => {
                      setIsRatesDirty(isDirty);
                      if (isDirty) onFormStateChange({ isDirty: true });
                    }}
                    customerRateDetailsRef={customerJobRateInputRef}
                    vendorOrDriverRateDetailsRef={vendorOrDriverJobRateInputRef}
                    disableCustomerRate={!isOrderOwner || !rateEditable.customer}
                    disableAssigneeRate={!rateEditable.assignee}
                    rateDisabledReasons={rateDisabledReasons}
                    hasInitialCustomerRate={hasInitialCustomerRate}
                    customerInitialRate={initialRates.customer ?? null}
                    fetchCustomerRates={fetchCustomerRates}
                    onCustomerRateFieldsChange={onCustomerRateFieldsChange}
                    fetchCustomerParentAddOnAssignments={fetchParentAddOnAssignments(
                      RateOwnerType.CUSTOMER,
                    )}
                    hasInitialVendorRate={hasInitialVendorRate}
                    hasInitialDriverRate={hasInitialDriverRate}
                    vendorInitialRate={initialRates.vendor ?? null}
                    driverInitialRate={initialRates.driver ?? null}
                    fetchVendorRates={fetchVendorRates}
                    fetchDriverRates={fetchDriverRates}
                    onAssigneeRateFieldsChange={onAssigneeRateFieldsChange}
                    fetchAssigneeParentAddOnAssignments={fetchParentAddOnAssignments(
                      isAssignedToVendor ? RateOwnerType.VENDOR : RateOwnerType.DRIVER,
                    )}
                    analyticsSurface="job_edit_form"
                  />
                </>
              )}
            </Card>

            <Card sx={{ p: 2, mb: 2 }}>
              <FileAttachments
                fileAttachableId={defaultJob?.id}
                fileAttachableType={FileAttachableType.JOB}
                control={control}
                errors={errors}
                form={formMethods}
                name="fileAttachments"
                ref={fileAttachmentsRef}
              />
            </Card>

            {defaultJob && (defaultJob.project?.id || defaultJob.order?.id) && (
              <Card sx={{ p: 2, pt: 0, mb: 2 }}>
                {defaultJob.project?.id && (
                  <FileAttachmentsReadOnly
                    label={t('form_fields.project_attachments')}
                    fileAttachableId={defaultJob.project.id}
                    fileAttachableType={FileAttachableType.PROJECT}
                  />
                )}
                {defaultJob.order?.id && (
                  <FileAttachmentsReadOnly
                    label={t('form_fields.order_attachments')}
                    fileAttachableId={defaultJob.order.id}
                    fileAttachableType={FileAttachableType.ORDER}
                  />
                )}
              </Card>
            )}

            {/* Notes */}
            <Notes defaultTab={NoteTypes.JOB} />
          </Grid>
        </Grid>
      </FormProvider>
    </Box>
  );
});

export { JobEditForm };
